import { assetsApi } from '@/api/endpoints';
import { ApiError } from '@/api/client';
import type { Asset } from '@/api/types';
import { parseAssetScan } from './assetScanKey';

export type RegistrationScan =
  | { kind: 'existing'; asset: Asset }
  | { kind: 'new'; value: string; tagShaped: boolean }
  | { kind: 'invalid'; message: string };

const SERIAL = /^[A-Za-z0-9._/-]{1,120}$/;
const TAG = /^[A-Z0-9-]{3,60}$/;

async function found(lookup: () => Promise<Asset>): Promise<Asset | null> {
  try { return await lookup(); } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/**
 * Decides what a scanned label means before anything is prefilled, so a device already in the
 * register is offered rather than added twice. Lookups are company-scoped by the server, which
 * answers another company's asset exactly like a missing one.
 */
export async function resolveRegistrationScan(raw: string, origin: string): Promise<RegistrationScan> {
  const value = raw.trim();
  const link = parseAssetScan(value, origin);
  if (link?.assetId) {
    const asset = await found(() => assetsApi.get(link.assetId));
    return asset ? { kind: 'existing', asset } : { kind: 'invalid', message: 'This label is not for an asset in your organisation’s register.' };
  }
  if (!link && (/^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith('/'))) {
    return { kind: 'invalid', message: 'This code links somewhere else. Scan a label issued by this site or the maker’s serial label.' };
  }
  const key = link?.assetTag ?? value;
  if (!SERIAL.test(key)) return { kind: 'invalid', message: 'This code is not an asset tag or serial number. Serials contain only letters, digits and . _ / -' };
  const tagShaped = TAG.test(key);
  const byTag = tagShaped ? await found(() => assetsApi.byTag(key)) : null;
  if (byTag) return { kind: 'existing', asset: byTag };
  const bySerial = await found(() => assetsApi.bySerial(key));
  if (bySerial) return { kind: 'existing', asset: bySerial };
  return { kind: 'new', value: key, tagShaped };
}
